const normalizeDate = (value) => String(value || "").substring(0, 10);
import { preserveReview } from './planningWorkspace.js';
export const PROGRAM_GROUPING_TOLERANCE_MINUTES = 60;

const normalizeTime = (value) => {
  const match = String(value || "").trim().match(/^(\d{1,2}):(\d{2})/);
  if (!match) return "";
  return `${match[1].padStart(2, "0")}:${match[2]}`;
};

const timeToMinutes = (value) => {
  const normalized = normalizeTime(value);
  const match = normalized.match(/^(\d{2}):(\d{2})$/);
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours > 23 || minutes > 59) return null;
  return (hours * 60) + minutes;
};

const regionKeyForPerson = (person, usesRegions = true) => {
  if (usesRegions && person?.regiao) return person.regiao;
  if (usesRegions && person?.cidade) return `${person.cidade} - Sem região`;
  return person?.cidade || person?.bairro || "Sem região";
};

const buildProgramRegionGroupsFromPeople = (people = [], { usesRegions = true, cityHasRegions } = {}) => {
  const mapa = {};
  for (const person of people) {
    const key = regionKeyForPerson(person, usesRegions && (!cityHasRegions || cityHasRegions(person.cidade)));
    if (!mapa[key]) mapa[key] = [];
    mapa[key].push(person);
  }

  const groups = Object.entries(mapa)
    .sort((a, b) => b[1].length - a[1].length || a[0].localeCompare(b[0], "pt-BR"))
    .map(([regiao, pessoas], index) => ({
      id: `regiao-pg-${index}-${Date.now()}`,
      regiao,
      pessoas,
      qtdVeiculos: 1,
      origens: [regiao],
      subgrupos: null,
    }));

  return groups;
};

const splitRegionGroupsIntoVanGroups = (gruposRegiao = [], getPersonIdentity) => {
  const novosGrupos = [];

  for (const gr of gruposRegiao) {
    const n = Number(gr.qtdVeiculos) || 1;
    if (!Number.isInteger(n) || n < 1 || n > gr.pessoas.length) throw new Error("Mantenha pelo menos um passageiro em cada van.");
    const subgruposOriginais = Array.isArray(gr.subgrupos) ? gr.subgrupos : [];

    if (subgruposOriginais.length > 0 && subgruposOriginais.length === n) {
      const pessoasPorSubgrupo = subgruposOriginais.map(() => []);
      const pessoaParaSubgrupo = new Map();

      subgruposOriginais.forEach((subgrupo, subIndex) => {
        (subgrupo.pessoas || []).forEach((person) => {
          pessoaParaSubgrupo.set(getPersonIdentity(person), subIndex);
        });
      });

      const pessoasSemHistorico = [];
      gr.pessoas.forEach((person) => {
        const originalIndex = pessoaParaSubgrupo.get(getPersonIdentity(person));
        if (Number.isInteger(originalIndex) && originalIndex >= 0 && originalIndex < pessoasPorSubgrupo.length) {
          pessoasPorSubgrupo[originalIndex].push(person);
        } else {
          pessoasSemHistorico.push(person);
        }
      });

      pessoasSemHistorico.forEach((person) => {
        let targetIndex = 0;
        for (let i = 1; i < pessoasPorSubgrupo.length; i += 1) {
          if (pessoasPorSubgrupo[i].length < pessoasPorSubgrupo[targetIndex].length) {
            targetIndex = i;
          }
        }
        pessoasPorSubgrupo[targetIndex].push(person);
      });

      pessoasPorSubgrupo.forEach((pessoasDoGrupo, subIndex) => {
        if (!pessoasDoGrupo.length) return;
        const original = subgruposOriginais[subIndex];
        novosGrupos.push({
          ...original,
          regiao: gr.regiao,
          pessoas: pessoasDoGrupo,
          veiculo: original.veiculo || null,
          motorista: original.motorista || null,
          programacao_responsavel_id: original.programacao_responsavel_id || null,
          programacao_codigo_responsavel: original.programacao_codigo_responsavel || null,
          planejamento_grupo_id: original.planejamento_grupo_id || null,
        });
      });
      continue;
    }

    if (n === 1) {
      novosGrupos.push({ regiao: gr.regiao, pessoas: gr.pessoas, veiculo: null, motorista: null });
      continue;
    }

    const porBairro = {};
    gr.pessoas.forEach((person) => {
      const bairro = person.bairro || "Sem bairro";
      if (!porBairro[bairro]) porBairro[bairro] = [];
      porBairro[bairro].push(person);
    });

    const pessoasOrdenadas = Object.entries(porBairro)
      .sort((a, b) => b[1].length - a[1].length)
      .flatMap(([, pessoas]) => pessoas);

    const buckets = [];
    let start = 0;
    for (let index = 0; index < n; index += 1) {
      const size = Math.ceil((pessoasOrdenadas.length - start) / (n - index));
      buckets.push(pessoasOrdenadas.slice(start,start + size));
      start += size;
    }

    buckets.forEach((pessoasDoGrupo) => {
      novosGrupos.push({ regiao: gr.regiao, pessoas: pessoasDoGrupo, veiculo: null, motorista: null });
    });
  }

  return novosGrupos;
};

const programDriver = (program) => {
  const id = Number(program?.motorista_responsavel_id);
  if (!Number.isInteger(id) || id <= 0) return null;
  return {
    id,
    nome: program.motorista_responsavel_nome,
    telefone: program.motorista_responsavel_telefone,
  };
};

const programVehicle = (program) => {
  const id = Number(program?.veiculo_responsavel_id);
  if (!Number.isInteger(id) || id <= 0) return null;
  return {
    id,
    veiculo: program.veiculo_responsavel_nome,
    placa: program.veiculo_responsavel_placa,
    capacidade: Math.max(1, Number(program.veiculo_responsavel_capacidade) || 15),
  };
};

const hasCompleteProgramExecutor = (program) => Boolean(
  programDriver(program) && programVehicle(program)
);

const attachProgramToVanGroup = (group, program, {
  usedMotoristaIds = [],
  usedVeiculoIds = [],
} = {}) => {
  if (!program) throw new Error("Selecione uma PG de referência para cada van.");
  if (!Array.isArray(group?.pessoas) || !group.pessoas.length) {
    throw new Error("Nenhuma van pode ficar sem passageiros.");
  }

  const driverAvailable = (driver) => driver && !usedMotoristaIds.includes(Number(driver.id));
  const vehicleAvailable = (vehicle) => vehicle
    && !usedVeiculoIds.includes(Number(vehicle.id))
    && Number(vehicle.capacidade || 15) >= group.pessoas.length;
  const inheritedDriver = programDriver(program);
  const inheritedVehicle = programVehicle(program);
  const currentVehicle = vehicleAvailable(group.veiculo)
    ? group.veiculo
    : null;
  const suggestedVehicle = currentVehicle || (vehicleAvailable(inheritedVehicle) ? inheritedVehicle : null);

  return preserveReview(group, {
    ...group,
    programacao_responsavel_id: Number(program.id),
    programacao_codigo_responsavel: program.codigo,
    data: normalizeDate(program.data),
    horario: normalizeTime(program.horario),
    // O pedido é demanda e pode não ter executor. Quando tiver, a van herda
    // essa sugestão; caso contrário o operador escolhe motorista e veículo na
    // etapa de atribuição, sem alterar o pedido de origem.
    motorista: driverAvailable(group.motorista)
      ? group.motorista
      : (group.planejamento_grupo_id ? null : driverAvailable(inheritedDriver) ? inheritedDriver : null),
    veiculo: group.planejamento_grupo_id && !group.veiculo ? null : suggestedVehicle,
    trecho: program.trecho,
  });
};

const attachProgramsToVanGroups = (vanGroups = [], programs = [], { reservedMotoristaIds = [], reservedVeiculoIds = [] } = {}) => {
  const sortedPrograms = [...programs].sort((a, b) => String(a.codigo).localeCompare(String(b.codigo), "pt-BR"));
  if (!vanGroups.length) throw new Error("Defina ao menos uma van para o planejamento.");
  if (!sortedPrograms.length) throw new Error("Selecione ao menos um pedido.");

  // Grupos maiores são associados primeiro. Priorizamos a PG que já sugere
  // motorista e veículo compatível; depois, motorista ou veículo isolados.
  // Qualquer lacuna permanece explícita para o operador completar na van.
  const referenceUsage = new Map();
  const usedMotoristaIds = [...reservedMotoristaIds];
  const usedVeiculoIds = [...reservedVeiculoIds];
  const assignments = new Array(vanGroups.length);
  const groupIndexes = vanGroups
    .map((group, index) => ({ index, size: Array.isArray(group?.pessoas) ? group.pessoas.length : 0 }))
    .sort((a, b) => b.size - a.size || a.index - b.index);

  for (const { index, size } of groupIndexes) {
    const group = vanGroups[index];
    const candidates = sortedPrograms
      .map((program) => ({
        program,
        currentReference: Number(group.programacao_responsavel_id) === Number(program.id),
        uses: referenceUsage.get(Number(program.id)) || 0,
        readyExecutor: hasCompleteProgramExecutor(program)
          && !usedMotoristaIds.includes(Number(program.motorista_responsavel_id))
          && !usedVeiculoIds.includes(Number(program.veiculo_responsavel_id))
          && programVehicle(program).capacidade >= size,
        hasDriver: Boolean(programDriver(program))
          && !usedMotoristaIds.includes(Number(program.motorista_responsavel_id)),
        hasCompatibleVehicle: Boolean(
          programVehicle(program) && programVehicle(program).capacidade >= size
          && !usedVeiculoIds.includes(Number(program.veiculo_responsavel_id))
        ),
        capacity: programVehicle(program)?.capacidade || null,
        ownPassengers: (group.pessoas || []).filter(
          (person) => Number(person.programacao_id) === Number(program.id)
        ).length,
      }))
      .sort((a, b) => (
        Number(b.currentReference) - Number(a.currentReference)
        || Number(a.uses > 0) - Number(b.uses > 0)
        || Number(b.readyExecutor) - Number(a.readyExecutor)
        || Number(b.hasDriver) - Number(a.hasDriver)
        || Number(b.hasCompatibleVehicle) - Number(a.hasCompatibleVehicle)
        || (a.capacity ?? Number.MAX_SAFE_INTEGER) - (b.capacity ?? Number.MAX_SAFE_INTEGER)
        || b.ownPassengers - a.ownPassengers
        || a.uses - b.uses
        || String(a.program.codigo).localeCompare(String(b.program.codigo), "pt-BR")
      ));

    const selected = candidates[0].program;
    const assigned = attachProgramToVanGroup(group, selected, { usedMotoristaIds, usedVeiculoIds });
    assignments[index] = assigned;
    referenceUsage.set(Number(selected.id), (referenceUsage.get(Number(selected.id)) || 0) + 1);
    if (assigned.motorista) usedMotoristaIds.push(Number(assigned.motorista.id));
    if (assigned.veiculo) usedVeiculoIds.push(Number(assigned.veiculo.id));
  }

  return assignments;
};

const buildSingleProgramVanPlan = (programs = [], people = []) => {
  if (!programs.length) throw new Error("Selecione ao menos um pedido.");
  const regioes = [...new Set(
    people.map((person) => person.regiao || person.cidade || "Sem região").filter(Boolean)
  )];
  const vanGroup = {
    regiao: regioes.length === 1 ? regioes[0] : regioes.slice(0, 3).join(" + "),
    pessoas: people,
    veiculo: null,
    motorista: null,
  };
  return attachProgramsToVanGroups([vanGroup], programs);
};

export const buildProgramPlanningContext = (programs = []) => {
  if (!Array.isArray(programs) || !programs.length) {
    throw new Error("Selecione ao menos um pedido.");
  }

  const dates = new Set(programs.map((program) => normalizeDate(program.data)));
  const legs = new Set(programs.map((program) => String(program.trecho || "")));
  const companies = new Set(programs.map((program) => String(program.empresa_destino_id || "")));
  if (dates.size !== 1 || legs.size !== 1 || companies.size !== 1) {
    throw new Error("Planeje juntos somente pedidos da mesma empresa, data e trecho.");
  }

  const times = programs.map((program) => ({
    value: normalizeTime(program.horario),
    minutes: timeToMinutes(program.horario),
  }));
  if (times.some((time) => time.minutes == null)) {
    throw new Error("Um ou mais pedidos possuem horário inválido.");
  }
  const timeValues = times.map((time) => time.minutes);
  const minTime = Math.min(...timeValues);
  const maxTime = Math.max(...timeValues);
  if ((maxTime - minTime) > PROGRAM_GROUPING_TOLERANCE_MINUTES) {
    throw new Error(
      `Planeje juntos somente pedidos com até ${PROGRAM_GROUPING_TOLERANCE_MINUTES} minutos de diferença.`
    );
  }

  for (const program of programs) {
    if (!Array.isArray(program.passageiros) || !program.passageiros.length) {
      throw new Error(`O pedido ${program.codigo} não possui passageiros.`);
    }
  }

  const people = programs.flatMap((program) => program.passageiros.map((passenger) => ({
    ...passenger,
    id: passenger.funcionario_id,
    programacao_id: Number(program.id),
    programacao_passageiro_id: Number(passenger.id),
    id_pedido_origem: program.codigo,
  })));
  return {
    programacaoIds: programs.map((program) => Number(program.id)),
    programs,
    pessoas: people,
    trecho: programs[0].trecho,
    data: normalizeDate(programs[0].data),
    horario: times.find((time) => time.minutes === minTime)?.value || programs[0].horario,
    empresaDestinoId: programs[0].empresa_destino_id || null,
    idPedido: programs.map((program) => program.codigo).sort((a, b) => a.localeCompare(b, "pt-BR")).join(", "),
    isTeste: programs.some((program) => Boolean(program.is_teste)),
    replanejarRotaId: [...new Set(programs.map((program) => Number(program.rota_operacional_id)).filter(Boolean))].length === 1
      ? Number(programs[0].rota_operacional_id) || null
      : null,
  };
};

export {
  buildProgramRegionGroupsFromPeople,
  splitRegionGroupsIntoVanGroups,
  attachProgramToVanGroup,
  attachProgramsToVanGroups,
  buildSingleProgramVanPlan,
  hasCompleteProgramExecutor,
};

// List rows have no passengers; final validation still uses fresh PG details and the API.
export const programSelectionReason = (candidate, selected = []) => {
  const others = selected.filter(program => Number(program.id) !== Number(candidate.id));
  if (!others.length) return '';
  const first = others[0];
  if (String(candidate.cliente_id || '') !== String(first.cliente_id || '')) return 'Outra transportadora';
  if (String(candidate.empresa_destino_id || '') !== String(first.empresa_destino_id || '')) return 'Outro destino';
  if (normalizeDate(candidate.data) !== normalizeDate(first.data)) return 'Outra data';
  if (candidate.trecho !== first.trecho) return 'Outro sentido';
  const times = [...others,candidate].map(program => timeToMinutes(program.horario));
  if (times.some(value => value == null)) return 'Horário inválido';
  return Math.max(...times) - Math.min(...times) > PROGRAM_GROUPING_TOLERANCE_MINUTES ? 'Intervalo maior que 1 hora' : '';
};

export const programPassengerKey = person => String(person.programacao_passageiro_id || `funcionario-${person.id}`);

export const moveRegionalPassengers = (regions, selectedKeys, targetId) => {
  const target = regions.find(region => region.id === targetId);
  if (!target || !selectedKeys.size) return regions;
  const moving = regions.filter(region => region.id !== targetId).flatMap(region => region.pessoas.filter(person => selectedKeys.has(programPassengerKey(person))));
  if (!moving.length) return regions;
  return regions.map(region => {
    const people = region.id === targetId ? [...region.pessoas,...moving] : region.pessoas.filter(person => !selectedKeys.has(programPassengerKey(person)));
    const reconcile = (group,members) => {
      if (!group.subgrupos?.length) return preserveReview(group,{ ...group,pessoas:members,...(group.qtdVeiculos != null ? {qtdVeiculos:Math.min(group.qtdVeiculos,members.length)} : {}) });
      const keys = new Set(members.map(programPassengerKey));
      const children = group.subgrupos.map(child => ({ ...child,pessoas:child.pessoas.filter(person => keys.has(programPassengerKey(person))) }));
      const assigned = new Set(children.flatMap(child => child.pessoas.map(programPassengerKey)));
      for (const person of members) if (!assigned.has(programPassengerKey(person))) {
        const child = children.reduce((best,item)=>item.pessoas.length<best.pessoas.length?item:best);
        child.pessoas.push(person);
      }
      return preserveReview(group,{ ...group,pessoas:members,subgrupos:children.filter(child=>child.pessoas.length).map(child=>reconcile(group.subgrupos[children.indexOf(child)],child.pessoas)) });
    };
    return { ...reconcile(region,people), qtdVeiculos: Math.min(Number(region.qtdVeiculos) || 1,people.length) };
  }).filter(region => region.pessoas.length);
};
